import { z } from 'zod';
import type { ChatbotConversationEntry, ChatbotRequest, ChatbotSkill } from '@ev-tech/search-modules-api';

import type { ConversationMessage, PageSkill } from '../types';
import { globalSkills } from '../skills/globalSkills';

export const entrySchema = z.object({
  message: z.string().min(1),
  messageType: z.enum(['USER', 'CHATBOT']),
  skillName: z.string().optional(),
  skillData: z.record(z.string(), z.string()).optional(),
});

export const responseSchema = z.object({ chatbotConversation: entrySchema.extend({ messageType: z.literal('CHATBOT') }) });

export const conversationSchema = z
  .object({
    conversation: z
      .array(
        entrySchema.extend({
          id: z.string().min(1),
          skillStatus: z
            .enum(['awaiting-confirmation', 'declined', 'executing', 'complete', 'component', 'unavailable', 'invalid', 'failed'])
            .optional(),
        }),
      )
      .refine((messages) => new Set(messages.map((message) => message.id)).size === messages.length),
    retryMessageId: z.string().nullable(),
  })
  .refine(
    ({ conversation, retryMessageId }) =>
      retryMessageId === null || (conversation.at(-1)?.id === retryMessageId && conversation.at(-1)?.messageType === 'USER'),
  );

export const resolveSkills = (pageSkills: readonly PageSkill[]): Map<string, PageSkill> => {
  const skills = new Map(globalSkills.map((skill) => [skill.type, skill]));

  for (const skill of pageSkills) skills.set(skill.type, skill);

  return skills;
};

export const isSkillDataValid = (skill: PageSkill, entry: ChatbotConversationEntry) =>
  (skill.responseSchema?.mandatoryFields ?? []).every(
    (field) => entry.skillData && Object.hasOwn(entry.skillData, field) && Boolean(entry.skillData[field]?.trim()),
  );

export const createChatbotRequest = (
  conversation: ConversationMessage[],
  { skills, context }: { skills: Map<string, PageSkill>; context?: ChatbotRequest['context'] },
): ChatbotRequest => {
  const pageSkills = Object.create(null) as Record<string, ChatbotSkill>;

  for (const [name, { description, responseSchema }] of skills) {
    pageSkills[name] = { description, ...(responseSchema && { responseSchema }) };
  }

  return {
    chatbotConversation: conversation.slice(-20).map(({ message, messageType, skillData, skillName }) => ({
      message,
      messageType,
      ...(skillData && { skillData }),
      ...(skillName && { skillName }),
    })),
    ...(context && { context }),
    pageSkills,
  };
};
